import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './AdminSupportCategories.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function AdminSupportCategories() {
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [fields, setFields] = useState([]);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [showFieldForm, setShowFieldForm] = useState(false);
  const [categoryFormData, setCategoryFormData] = useState({
    name: '',
    description: '',
    required_role: 'visitor',
    order_index: 0
  });
  const [fieldFormData, setFieldFormData] = useState({
    field_name: '',
    field_type: 'text',
    field_options: '',
    is_required: false,
    order_index: 0
  });
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (selectedCategory) {
      fetchFields(selectedCategory.id);
    }
  }, [selectedCategory]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/support/categories`, { withCredentials: true });
      setCategories(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setLoading(false);
    }
  };

  const fetchFields = async (categoryId) => {
    try {
      const response = await axios.get(`${API_URL}/api/support/categories/${categoryId}/fields`, { withCredentials: true });
      setFields(response.data);
    } catch (error) {
      console.error('Error fetching fields:', error);
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await axios.put(`${API_URL}/api/support/categories/${editingCategory.id}`, 
          categoryFormData,
          { withCredentials: true }
        );
        alert('Category updated successfully!');
      } else {
        await axios.post(`${API_URL}/api/support/categories`, 
          categoryFormData,
          { withCredentials: true }
        );
        alert('Category created successfully!');
      }
      
      fetchCategories();
      resetCategoryForm();
    } catch (error) {
      alert('Error saving category: ' + error.response?.data?.error);
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryFormData({
      name: category.name,
      description: category.description || '',
      required_role: category.required_role,
      order_index: category.order_index
    });
    setShowCategoryForm(true);
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Delete this category and all its fields?')) return;

    try {
      await axios.delete(`${API_URL}/api/support/categories/${categoryId}`, { withCredentials: true });
      alert('Category deleted successfully!');
      fetchCategories();
      if (selectedCategory?.id === categoryId) {
        setSelectedCategory(null);
        setFields([]);
      }
    } catch (error) {
      alert('Error deleting category: ' + error.response?.data?.error);
    }
  };

  const resetCategoryForm = () => {
    setCategoryFormData({
      name: '',
      description: '',
      required_role: 'visitor',
      order_index: 0
    });
    setEditingCategory(null);
    setShowCategoryForm(false);
  };

  const handleCreateField = async (e) => {
    e.preventDefault();
    if (!selectedCategory) {
      alert('Please select a category first');
      return;
    }

    try {
      if (editingField) {
        await axios.put(`${API_URL}/api/support/categories/fields/${editingField.id}`, 
          fieldFormData,
          { withCredentials: true }
        );
        alert('Field updated successfully!');
      } else {
        await axios.post(`${API_URL}/api/support/categories/${selectedCategory.id}/fields`, 
          fieldFormData,
          { withCredentials: true }
        );
        alert('Field created successfully!');
      }
      
      fetchFields(selectedCategory.id);
      resetFieldForm();
    } catch (error) {
      alert('Error saving field: ' + error.response?.data?.error);
    }
  };

  const handleEditField = (field) => {
    setEditingField(field);
    setFieldFormData({
      field_name: field.field_name,
      field_type: field.field_type,
      field_options: field.field_options || '',
      is_required: field.is_required,
      order_index: field.order_index
    });
    setShowFieldForm(true);
  };

  const handleDeleteField = async (fieldId) => {
    if (!window.confirm('Delete this field?')) return;

    try {
      await axios.delete(`${API_URL}/api/support/categories/fields/${fieldId}`, { withCredentials: true });
      alert('Field deleted successfully!');
      fetchFields(selectedCategory.id);
    } catch (error) {
      alert('Error deleting field: ' + error.response?.data?.error);
    }
  };

  const resetFieldForm = () => {
    setFieldFormData({
      field_name: '',
      field_type: 'text',
      field_options: '',
      is_required: false,
      order_index: 0
    });
    setEditingField(null);
    setShowFieldForm(false);
  };

  return (
    <div className="admin-support-categories">
      <div className="panel-header">
        <h1>🎫 Manage Support Categories</h1>
        <p>Create categories with custom fields for support tickets</p>
      </div>

      <div className="info-box glass" style={{ padding: '20px', marginBottom: '24px', borderLeft: '4px solid var(--primary-blue)' }}>
        <h3 style={{ margin: '0 0 12px 0', color: 'var(--secondary-blue)' }}>💡 How to use Custom Fields</h3>
        <ul style={{ margin: 0, paddingLeft: '20px', color: 'rgba(255, 255, 255, 0.8)' }}>
          <li>Create a category (e.g., "Purchase Issue", "Bug Report")</li>
          <li>Click "Manage Fields" to add custom input fields</li>
          <li>Add fields like: Email, Transaction ID, CFX Name, Server ID, etc.</li>
          <li>When users create tickets in this category, they'll fill out these fields</li>
          <li>Field types: Text, Textarea, Number, Select (dropdown), Checkbox</li>
        </ul>
      </div>

      <div className="glass" style={{ padding: '24px', marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2>Categories</h2>
          <button onClick={() => setShowCategoryForm(true)} className="btn btn-primary">
            Add Category
          </button>
        </div>

        {showCategoryForm && (
          <div className="form-section glass">
            <h3>{editingCategory ? 'Edit Category' : 'Add Category'}</h3>
            <form onSubmit={handleCreateCategory}>
              <div className="form-group">
                <label>Category Name *</label>
                <input
                  type="text"
                  placeholder="e.g., Script Issue, Purchase Question"
                  value={categoryFormData.name}
                  onChange={(e) => setCategoryFormData({ ...categoryFormData, name: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  placeholder="Brief description of this category"
                  value={categoryFormData.description}
                  onChange={(e) => setCategoryFormData({ ...categoryFormData, description: e.target.value })}
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label>Required Role</label>
                <select
                  value={categoryFormData.required_role}
                  onChange={(e) => setCategoryFormData({ ...categoryFormData, required_role: e.target.value })}
                >
                  <option value="visitor">Visitor (All Users)</option>
                  <option value="admin">Admin Only</option>
                </select>
                <small>Only users with this role or higher will see this category</small>
              </div>

              <div className="form-group">
                <label>Order Index</label>
                <input
                  type="number"
                  value={categoryFormData.order_index}
                  onChange={(e) => setCategoryFormData({ ...categoryFormData, order_index: e.target.value })}
                />
                <small>Lower numbers appear first</small>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary">
                  {editingCategory ? 'Update Category' : 'Create Category'}
                </button>
                <button type="button" onClick={resetCategoryForm} className="btn btn-secondary">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="categories-list">
          {loading ? (
            <div className="loading">Loading categories...</div>
          ) : categories.length === 0 ? (
            <div className="empty-state">
              <p>No categories yet. Create your first support category!</p>
            </div>
          ) : (
            categories
              .sort((a, b) => a.order_index - b.order_index)
              .map((category) => (
                <div 
                  key={category.id} 
                  className={`category-card glass ${selectedCategory?.id === category.id ? 'selected' : ''}`}
                >
                  <div className="category-card-header">
                    <div>
                      <h3>{category.name}</h3>
                      {category.description && <p className="category-desc">{category.description}</p>}
                      <div className="category-meta">
                        <span className={`role-badge ${category.required_role}`}>
                          {category.required_role === 'admin' ? '👑 Admin Only' : '👥 All Users'}
                        </span>
                        <span className="order-badge">Order: {category.order_index}</span>
                      </div>
                    </div>
                    <div className="category-card-actions">
                      <button 
                        onClick={() => setSelectedCategory(selectedCategory?.id === category.id ? null : category)} 
                        className={`btn ${selectedCategory?.id === category.id ? 'btn-primary' : 'btn-secondary'}`}
                      >
                        {selectedCategory?.id === category.id ? 'Selected' : 'Manage Fields'}
                      </button>
                      <button onClick={() => handleEditCategory(category)} className="btn btn-secondary">
                        Edit
                      </button>
                      <button onClick={() => handleDeleteCategory(category.id)} className="btn btn-danger">
                        Delete
                      </button>
                    </div>
                  </div>

                  {selectedCategory?.id === category.id && (
                    <div className="fields-section">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px', marginBottom: '12px' }}>
                        <h4>Custom Fields</h4>
                        <button onClick={() => setShowFieldForm(true)} className="btn btn-primary btn-sm">
                          Add Field
                        </button>
                      </div>

                      {showFieldForm && (
                        <div className="field-form glass">
                          <h5>{editingField ? 'Edit Field' : 'Add Custom Field'}</h5>
                          <p style={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.7)', marginBottom: '16px' }}>
                            💡 Examples: Email, Transaction ID, CFX Name, Server ID, Discord Username, etc.
                          </p>
                          <form onSubmit={handleCreateField}>
                            <div className="form-group">
                              <label>Field Name *</label>
                              <input
                                type="text"
                                placeholder="e.g., Email Address, Transaction ID, CFX Name"
                                value={fieldFormData.field_name}
                                onChange={(e) => setFieldFormData({ ...fieldFormData, field_name: e.target.value })}
                                required
                              />
                              <small style={{ color: 'rgba(255, 255, 255, 0.6)' }}>This label will be shown to users</small>
                            </div>

                            <div className="form-group">
                              <label>Field Type *</label>
                              <select
                                value={fieldFormData.field_type}
                                onChange={(e) => setFieldFormData({ ...fieldFormData, field_type: e.target.value })}
                              >
                                <option value="text">Text (single line)</option>
                                <option value="textarea">Textarea (multiple lines)</option>
                                <option value="number">Number</option>
                                <option value="select">Select (dropdown)</option>
                                <option value="checkbox">Checkbox</option>
                              </select>
                            </div>

                            {fieldFormData.field_type === 'select' && (
                              <div className="form-group">
                                <label>Options (comma separated)</label>
                                <input
                                  type="text"
                                  placeholder="Option 1, Option 2, Option 3"
                                  value={fieldFormData.field_options}
                                  onChange={(e) => setFieldFormData({ ...fieldFormData, field_options: e.target.value })}
                                />
                              </div>
                            )}

                            <div className="form-group">
                              <label className="checkbox-label">
                                <input
                                  type="checkbox"
                                  checked={fieldFormData.is_required}
                                  onChange={(e) => setFieldFormData({ ...fieldFormData, is_required: e.target.checked })}
                                />
                                Required Field
                              </label>
                            </div>

                            <div className="form-group">
                              <label>Order Index</label>
                              <input
                                type="number"
                                value={fieldFormData.order_index}
                                onChange={(e) => setFieldFormData({ ...fieldFormData, order_index: e.target.value })}
                              />
                            </div>

                            <div className="form-actions">
                              <button type="submit" className="btn btn-primary btn-sm">
                                {editingField ? 'Update Field' : 'Create Field'}
                              </button>
                              <button type="button" onClick={resetFieldForm} className="btn btn-secondary btn-sm">
                                Cancel
                              </button>
                            </div>
                          </form>
                        </div>
                      )}

                      {fields.length === 0 ? (
                        <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem' }}>
                          No custom fields yet. Add fields that users must fill when creating a ticket.
                        </p>
                      ) : (
                        <div className="fields-list">
                          {fields
                            .sort((a, b) => a.order_index - b.order_index)
                            .map((field) => (
                              <div key={field.id} className="field-item">
                                <div className="field-info">
                                  <strong>{field.field_name}</strong>
                                  <div className="field-badges">
                                    <span className="type-badge">{field.field_type}</span>
                                    {field.is_required && <span className="required-badge">Required</span>}
                                    <span className="order-badge">Order: {field.order_index}</span>
                                  </div>
                                  {field.field_options && (
                                    <small className="field-options">Options: {field.field_options}</small>
                                  )}
                                </div>
                                <div className="field-actions">
                                  <button onClick={() => handleEditField(field)} className="btn btn-secondary btn-sm">
                                    Edit
                                  </button>
                                  <button onClick={() => handleDeleteField(field.id)} className="btn btn-danger btn-sm">
                                    Delete
                                  </button>
                                </div>
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminSupportCategories;
